import * as React from "react";

import { History, Keyboard, Link2, MapPin, MousePointer2, Plus, RotateCcw, Sparkles, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  apiErrorMessage,
  createBookingPage,
  draftBookingDescription,
  getEntitlements,
  getMyBookingLimits,
  listIntegrations,
  listAvailabilityPatterns,
  previewBookingSlots,
} from "@/api/client";
import {
  BookingPageUpsertRequest,
  BookingPageView,
  BookingPreviewSlot,
  BookingWindow,
  ConferenceProvider,
  AvailabilityPatternView,
  MyBookingLimits,
} from "@/api/types";
import { ClockTimePicker } from "@/components/ClockTimePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { cn } from "@/lib/utils";

import { AvailabilityCanvas } from "./AvailabilityCanvas";
import { AvailabilityCalendar } from "./AvailabilityCalendar";
import { BlockedSlot, BookingCalendar } from "./BookingCalendar";
import { DateRangeField } from "./DateRangeField";
import { DurationField } from "./DurationField";
import { FillFromPatternMenu } from "./FillFromPatternMenu";
import { MeetingLocationPicker } from "./MeetingLocationPicker";
import { BuilderSocialNote } from "./BuilderSocialNote";
import { ToolbarPopover } from "./ToolbarPopover";
import { markBuilderReturn } from "./builderReturn";
import { clearDraft, draftHasContent, loadDraft, saveDraft } from "./draft";
import { fillRun } from "./slotEngine";
import { expandRangesToWindows, normalizeWindows } from "./windows";
import { VideoOption, conferenceProviderLabel, videoOptions } from "@/lib/calendarProviders";

/** The same faint dot-grid the Availability canvas uses, so every panel shares one texture. */
const DOTTED_CANVAS: React.CSSProperties = {
  backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1.3px)",
  backgroundSize: "14px 14px",
};

/** One toggle row in the Options panel: title + hint on the left, a Switch on the right. */
function OptionRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
  children,
}: {
  label: string;
  /** Optional — prefer folding the explanation into the label so the row reads at a glance. */
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("flex items-start justify-between gap-3", disabled && "opacity-60")}>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug text-foreground">{label}</p>
        {hint && <p className="text-sm leading-snug text-brand-blue">{hint}</p>}
        {children}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-0.5 shrink-0"
      />
    </div>
  );
}

/**
 * One uniform titled section. Every panel uses the same header — an uppercase, letter-spaced
 * caps heading at the top (the "OPTIONS" treatment) with an optional right-aligned action.
 *
 * By default it's a standalone card with the calm canvas background (no outline). Pass
 * `grouped` to drop the background/rounding so several panels can share ONE canvas-tinted
 * container separated by thin `divide-y` rules — a lighter, flowier look than boxing each
 * one, and it reclaims the margins that separate boxes would waste.
 */
function Panel({
  id,
  title,
  action,
  grouped,
  className,
  bodyClassName,
  children,
}: {
  id?: string;
  title?: string;
  action?: React.ReactNode;
  grouped?: boolean;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className={cn(grouped ? "scroll-mt-20 p-3" : "scroll-mt-20 rounded-xl bg-muted/40 p-4", className)}
      style={grouped ? undefined : DOTTED_CANVAS}
    >
      {(title || action) && (
        <div className="mb-2 flex min-h-[1.5rem] items-center justify-between gap-2">
          {/* Section heading: dark + sharp caps (reads clearly on the dotted canvas panels). */}
          {title && <h3 className="text-xs font-bold uppercase tracking-wide text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/**
 * The "tokens used" pill that sits next to the AI button. It count-ups to the new figure and
 * gives a quick pulse whenever a fresh draft lands, so the cost of each AI run is visible in the
 * moment it happens rather than being a static number tucked under the field.
 */
function TokenBadge({ value }: { value: number }) {
  const [display, setDisplay] = React.useState(value);
  const [pulse, setPulse] = React.useState(false);
  const prev = React.useRef(value);

  React.useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;
    if (from === to) return;

    setPulse(true);
    const pulseTimer = setTimeout(() => setPulse(false), 550);

    const dur = 650;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(pulseTimer);
    };
  }, [value]);

  return (
    <span
      title="Tokens the last AI draft used"
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-contrast/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-brand-contrast transition-transform duration-300",
        pulse && "scale-110",
      )}
    >
      <Sparkles className="h-3 w-3" />~{display.toLocaleString()} tokens
    </span>
  );
}

function minutesBetween(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
}

/** Local-midnight epoch ms of the day an ISO instant falls on. */
function startOfDay(iso: string): number {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Minutes from local midnight of an ISO instant. */
function minuteOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/** A window on the given day (local-midnight ms) spanning [startMin, endMin) minutes-from-midnight. */
function windowFromDayMinutes(dayMs: number, startMin: number, endMin: number): BookingWindow {
  const s = new Date(dayMs);
  s.setHours(0, startMin, 0, 0);
  const e = new Date(dayMs);
  e.setHours(0, endMin, 0, 0);
  return { start: s.toISOString(), end: e.toISOString() };
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Tiers below Pro are the ones we nudge to upgrade for longer links. */
function canUpgradeLinkLength(tier: string): boolean {
  return tier === "FREE" || tier === "BASIC";
}

function titleCase(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

/** Full-word duration for the settings summary chips ("30 minutes" / "1 hour"). */
function durationText(minutes: number): string {
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} hour${h > 1 ? "s" : ""}`;
  }
  return `${minutes} minutes`;
}

/** Scroll a settings panel into view and flash a ring, so a summary chip can point at it. */
function jumpToSetting(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-brand-teal", "ring-offset-2", "rounded-xl");
  window.setTimeout(
    () => el.classList.remove("ring-2", "ring-brand-teal", "ring-offset-2", "rounded-xl"),
    1200,
  );
}

function fmtWindow(w: BookingWindow): string {
  const start = new Date(w.start);
  const end = new Date(w.end);
  const day = start.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${t(start)}–${t(end)}`;
}

/**
 * Create or edit a booking page: name it, set a booking duration + how long the
 * link accepts responses, and drag availability out on the full-width calendar.
 * Each drag becomes a block; save turns them into a shareable link.
 */
export function BookingPageBuilder({
  existing,
  onSaved,
  onCancel,
  onError,
}: {
  existing?: BookingPageView;
  onSaved: (page: BookingPageView) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  // Per-link branding. Hydrated from the link being edited, so opening an existing one does not
  // quietly switch a host's choice back on; a new link starts with both on.
  const [showHostAvatar, setShowHostAvatar] = React.useState(existing?.show_host_avatar ?? true);
  const [showHostBanner, setShowHostBanner] = React.useState(existing?.show_host_banner ?? true);
  // A NEW page autosaves to sessionStorage (see ./draft); on return/reload we refill from it,
  // so a click that navigates away (e.g. opening a meeting on the Meetings calendar) never
  // loses work. Read once — an existing (frozen) page never has a draft.
  const draft0 = React.useMemo(() => (existing ? null : loadDraft()), [existing]);
  const [restored, setRestored] = React.useState(() => draftHasContent(draft0));
  const [fading, setFading] = React.useState(false);

  // The "Draft restored" banner is a reassurance, not an action — it clears itself: stays fully
  // visible ~3.5s, then fades out slowly, and unmounts the instant that fade ends (onTransitionEnd
  // below) so there's no invisible-but-present gap. (The draft and Discard stay; X dismisses early.)
  React.useEffect(() => {
    if (!restored) return;
    setFading(false);
    const fadeAt = window.setTimeout(() => setFading(true), 2500);
    // Fallback removal in case the transition never fires (e.g. the tab was backgrounded).
    const removeAt = window.setTimeout(() => setRestored(false), 5500);
    return () => {
      window.clearTimeout(fadeAt);
      window.clearTimeout(removeAt);
    };
  }, [restored]);

  const [title, setTitle] = React.useState(existing?.title ?? draft0?.title ?? "");
  const [description, setDescription] = React.useState(existing?.description ?? draft0?.description ?? "");
  const [slotMinutes, setSlotMinutes] = React.useState(existing?.slot_minutes ?? draft0?.slotMinutes ?? 30);
  // Draw-cursor size (minutes): the block a plain click / hover cursor places when hand-drawing.
  // Independent of the booking (fill) duration — it only sizes the drawing cursor, and isn't saved.
  const [cursorMinutes, setCursorMinutes] = React.useState(30);
  // Gap between fill slots (minutes; 0 = back-to-back). A Fill-only setting, matching the rolling
  // builder's Fill menu — it just spaces the laid-down slots and isn't stored as a page field.
  const [gapMinutes, setGapMinutes] = React.useState(0);
  const [maxBookings, setMaxBookings] = React.useState<string>(
    existing?.max_bookings != null ? String(existing.max_bookings) : (draft0?.maxBookings ?? ""),
  );
  // Max bookings defaults to "as many as there are slots" and follows the slot count until the
  // host types their own cap — an existing page's saved cap counts as already chosen.
  const [maxTouched, setMaxTouched] = React.useState(
    existing?.max_bookings != null || (draft0?.maxTouched ?? false),
  );
  const [oneEach, setOneEach] = React.useState(existing?.one_per_invitee ?? draft0?.oneEach ?? false);
  const [allowCancel, setAllowCancel] = React.useState(existing?.allow_cancel ?? draft0?.allowCancel ?? true);
  // Invite-signups needs a discoverable host (it surfaces your @handle). Read the
  // AI drafting is a Pro/Enterprise feature. The button is shown to everyone (so free users
  // discover it) but gated: an unentitled click routes to /upgrade instead of drafting.
  const [aiEntitled, setAiEntitled] = React.useState(false);
  const [aiDrafting, setAiDrafting] = React.useState(false);
  // Tokens the last AI draft cost, shown as a small usage note (like the chat footer).
  const [lastDraftTokens, setLastDraftTokens] = React.useState<number | null>(null);
  const navigate = useNavigate();
  // The link is always live from now — validFrom is pinned to today and never user-set. (A stale
  // draft's validFrom is deliberately ignored so it can't seed a future start.)
  const [validFrom, setValidFrom] = React.useState(
    existing?.valid_from ? existing.valid_from.slice(0, 10) : todayIso(),
  );
  // No validUntil state any more. When the link closes is the server's to decide — the earlier of
  // its last offered time and the plan's window — because the same date also bounded the free/busy
  // query, so a host choosing "keep it live a year" was silently turning their own double-booking
  // guard off. Ending a link early is the Expire action, which is what hosts reached for anyway.
  const [windows, setWindows] = React.useState<BookingWindow[]>(
    existing?.windows ?? draft0?.windows ?? [],
  );
  // An availability pattern is a convenient way to FILL the availability as a starting point: its ranges
  // are stamped into the calendar as ordinary slots you can then prune. No live link — once a
  // link is created the slots are frozen (see the server's create-time snapshot).
  const [availabilityPatterns, setAvailabilityPatterns] = React.useState<AvailabilityPatternView[]>([]);
  // The armed "fill from" source type (persisted so the dropdown shows it). Selecting it no
  // longer auto-stamps the horizon — filling is manual + view-scoped (Fill this week/month).
  const [filledTypeId, setFilledTypeId] = React.useState<string>(draft0?.filledTypeId ?? "");
  // The calendar's current view + visible span, so Fill/Clear can scope to "this week/month".
  const [calScope, setCalScope] = React.useState<{ view: string; from: string; to: string } | null>(
    null,
  );
  // The slots the last Clear removed — kept so a single Undo can put them back exactly. It's shown as
  // a transient overlay chip inside the calendar (not in the page flow), self-clearing on the same
  // timing as the "Draft restored" chip: fully visible ~2.5s, then a slow fade, then unmount.
  const [lastCleared, setLastCleared] = React.useState<BookingWindow[] | null>(null);
  const [clearedFading, setClearedFading] = React.useState(false);
  React.useEffect(() => {
    if (!lastCleared) return;
    setClearedFading(false);
    const fadeAt = window.setTimeout(() => setClearedFading(true), 2500);
    const removeAt = window.setTimeout(() => setLastCleared(null), 5500);
    return () => {
      window.clearTimeout(fadeAt);
      window.clearTimeout(removeAt);
    };
  }, [lastCleared]);
  // Live "what invitees see" preview — the server runs the real slot pipeline (windows − busy)
  // on the draft, so this matches what gets frozen at creation.
  const [previewSlots, setPreviewSlots] = React.useState<BookingPreviewSlot[]>([]);
  // Slots the preview dropped for clashing with a meeting — shown as disabled conflict markers.
  const [blockedSlots, setBlockedSlots] = React.useState<BlockedSlot[]>([]);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [limits, setLimits] = React.useState<MyBookingLimits | null>(null);
  // Type-a-block-by-hand fields (an alternative to dragging on the calendar).
  const [typedDate, setTypedDate] = React.useState<string>(() => draft0?.typedDate ?? todayIso());
  const [typedDateEnd, setTypedDateEnd] = React.useState<string>(() => draft0?.typedDateEnd ?? todayIso());
  const [typedFrom, setTypedFrom] = React.useState(draft0?.typedFrom ?? "09:00");
  const [typedTo, setTypedTo] = React.useState(draft0?.typedTo ?? "17:00");
  const [typedError, setTypedError] = React.useState<string | null>(null);
  // On by default: the host almost always wants to see their existing meetings while
  // drawing availability, since any slot that clashes with one is dropped from the link.
  const [showMine, setShowMine] = React.useState(draft0?.showMine ?? true);

  // The host's busy intervals (confirmed meetings / bookings / Google events) for the visible range,
  // reported up from the calendar. Fill-from-a-type skips these so it never lays a slot over a
  // confirmed meeting (a stable callback keeps the calendar's busy-load effect from looping).
  const busyRef = React.useRef<{ start: number; end: number }[]>([]);
  const handleBusyChange = React.useCallback((busy: { start: number; end: number }[]) => {
    busyRef.current = busy;
  }, []);

  // Meeting location, chosen in one picker but holding ONE OF EACH type at once: a video provider
  // (a per-booking link minted at confirmation if connected) AND/OR a physical place. Independent.
  const [conferencingProvider, setConferencingProvider] = React.useState<string>(
    existing?.conferencing_provider ?? draft0?.conferencingProvider ?? "",
  );
  const [meetingLocation, setMeetingLocation] = React.useState<string>(
    existing?.location ?? draft0?.location ?? "",
  );
  const [physicalOn, setPhysicalOn] = React.useState<boolean>(
    existing ? existing.location != null : (draft0?.physicalOn ?? false),
  );
  // A video link rides on a calendar event, so which options are actually offerable depends on
  // which calendar the host has connected. Derived centrally so both builders agree. Null until
  // we know: telling a host with a working calendar that it "isn't enabled here" is worse than
  // showing nothing for the moment it takes to read.
  const [videoChoices, setVideoChoices] = React.useState<VideoOption[] | null>(null);
  React.useEffect(() => {
    listIntegrations()
      .then((v) => setVideoChoices(videoOptions(v)))
      .catch(() => {
        // Non-fatal, but don't leave the picker loading forever: fall back to offering nothing,
        // which still lets them pick a physical location or no meeting location at all.
        setVideoChoices(videoOptions(null));
      });
  }, []);

  // Autosave the in-progress NEW page on every change, so leaving (a calendar click that
  // navigates to /meetings) or a reload comes back to exactly this state. Session-scoped.
  React.useEffect(() => {
    if (existing) return;
    saveDraft({
      title,
      description,
      slotMinutes,
      maxBookings,
      maxTouched,
      oneEach,
      allowCancel,
      validFrom,
      windows,
      filledTypeId,
      conferencingProvider,
      physicalOn,
      location: meetingLocation,
      typedDate,
      typedDateEnd,
      typedFrom,
      typedTo,
      showMine,
    });
  }, [
    existing,
    title,
    description,
    slotMinutes,
    maxBookings,
    maxTouched,
    oneEach,
    allowCancel,
    validFrom,
    windows,
    filledTypeId,
    conferencingProvider,
    physicalOn,
    meetingLocation,
    typedDate,
    typedDateEnd,
    typedFrom,
    typedTo,
    showMine,
  ]);

  // Throw away the restored draft and start clean (the "Discard" on the restore banner).
  const discardDraft = () => {
    clearDraft();
    setRestored(false);
    setTitle("");
    setDescription("");
    setSlotMinutes(30);
    setMaxBookings("");
    setMaxTouched(false);
    setOneEach(false);
    setAllowCancel(true);
    setValidFrom(todayIso());
    setWindows([]);
    setFilledTypeId("");
    setConferencingProvider("");
    setPhysicalOn(false);
    setMeetingLocation("");
    setLastCleared(null);
    setTypedDate(todayIso());
    setTypedDateEnd(todayIso());
    setTypedFrom("09:00");
    setTypedTo("17:00");
    setShowMine(true);
  };

  // Seed the description editor's height to match the settings rail (so the two line up), while
  // keeping it drag-resizable. A textarea can't flex-fill AND resize on the same axis, so we set
  // an explicit height imperatively instead of via flex; once the host drags the handle we back
  // off and leave their height alone.
  const railRef = React.useRef<HTMLDivElement>(null);
  const descColRef = React.useRef<HTMLDivElement>(null);
  const descTaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const descUserResized = React.useRef(false);

  React.useLayoutEffect(() => {
    const rail = railRef.current;
    const col = descColRef.current;
    const ta = descTaRef.current;
    if (!rail || !col || !ta) return;
    const applySeed = () => {
      if (descUserResized.current) return;
      // chrome = everything in the description column that isn't the textarea (title, footnote,
      // the editor's toolbar/footer, paddings). Making the textarea (railHeight − chrome) tall
      // makes the whole column match the rail, so their bottom borders line up.
      const chrome = col.offsetHeight - ta.offsetHeight;
      const target = Math.max(160, Math.round(rail.offsetHeight - chrome));
      ta.style.height = `${target}px`;
    };
    applySeed();
    // Re-seed once web fonts settle — text can reflow (notes wrapping) after first paint, which
    // changes the rail height and would otherwise leave the description a little short/tall.
    document.fonts?.ready.then(() => applySeed()).catch(() => {});
    const ro = new ResizeObserver(() => applySeed());
    ro.observe(rail);
    const onResize = () => applySeed();
    window.addEventListener("resize", onResize);
    // Grabbing the resize handle (bottom-right corner) means "I'll pick my own height" — from
    // then on we stop re-seeding and leave the host's chosen height alone.
    const onDown = (e: PointerEvent) => {
      const r = ta.getBoundingClientRect();
      if (e.clientX >= r.right - 22 && e.clientY >= r.bottom - 22) descUserResized.current = true;
    };
    ta.addEventListener("pointerdown", onDown);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      ta.removeEventListener("pointerdown", onDown);
    };
  }, []);

  // Every add flows through here: normalise (dedupe + merge true overlaps) and drop
  // any block entirely in the past. The range cap isn't enforced here — going over
  // is allowed so you can see everything (e.g. after splitting into slots); Save is
  // what blocks until you're back within the plan.
  const applyWindows = (next: BookingWindow[]) => {
    const now = Date.now();
    const norm = normalizeWindows(next).filter((w) => new Date(w.end).getTime() > now);
    setWindows(norm);
  };

  React.useEffect(() => {
    getMyBookingLimits()
      .then(setLimits)
      .catch(() => {
        /* non-fatal — the server still validates on save */
      });
  }, []);

  React.useEffect(() => {
    getEntitlements()
      .then((e) => {
        setAiEntitled(e.tier === "PRO" || e.tier === "ENTERPRISE");
      })
      .catch(() => {
        /* non-fatal — the server enforces the gates on save/draft */
      });
  }, []);

  React.useEffect(() => {
    listAvailabilityPatterns()
      .then(setAvailabilityPatterns)
      .catch(() => {
        /* non-fatal — the selector just won't offer types */
      });
  }, []);

  // Fill the calendar from an availability pattern: expand its ranges over the link's date window into
  // ordinary, editable, deletable slots. A starting point you then prune. Switching type swaps
  // the previous fill's slots out (by key) for the new one's, leaving hand-drawn slots intact;
  // choosing "None" just removes the fill.
  // The dropdown only ARMS the source type now — it no longer auto-stamps the whole horizon.
  // Filling is manual and view-scoped (Fill this week/month), so a cleared week is never
  // silently re-added by re-picking a type or nudging the dates.
  const selectType = (typeId: string) => setFilledTypeId(typeId);

  // --- Bulk fill / clear, scoped to a date range ------------------------------------------
  // month / day / week — the rolling "From today" view is a 7-day week, so it reads "week" too.
  const viewNoun = (view?: string): string =>
    view === "dayGridMonth" ? "month" : view === "timeGridDay" ? "day" : "week";
  // Reference date for week/month bounds: the MIDPOINT of the visible span, else today. Midpoint
  // (not the span start) so a month grid — whose visible range starts on a spillover day of the
  // previous month — still anchors inside the month actually on screen.
  const scopeAnchor = (): Date =>
    calScope ? new Date((Date.parse(calScope.from) + Date.parse(calScope.to)) / 2) : new Date();
  const weekBounds = (anchor: Date): [number, number] => {
    const start = new Date(anchor);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // back to Monday
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return [start.getTime(), end.getTime()];
  };
  const monthBounds = (anchor: Date): [number, number] => [
    new Date(anchor.getFullYear(), anchor.getMonth(), 1).getTime(),
    new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1).getTime(),
  ];

  const clearRange = (fromMs: number, toMs: number, label: string) => {
    const inScope = windows.filter((w) => {
      const s = Date.parse(w.start);
      return s >= fromMs && s < toMs;
    });
    if (inScope.length === 0) return;
    const plural = inScope.length === 1 ? "slot" : "slots";
    if (!window.confirm(`Clear ${inScope.length} ${plural} ${label}? You can undo this.`)) return;
    setLastCleared(inScope);
    applyWindows(
      windows.filter((w) => {
        const s = Date.parse(w.start);
        return !(s >= fromMs && s < toMs);
      }),
    );
  };
  const fillRange = (fromMs: number, toMs: number, typeId?: string) => {
    const type = availabilityPatterns.find((x) => x.id === (typeId ?? filledTypeId));
    if (!type) return;
    const lo = Math.max(fromMs, Date.now(), validFrom ? Date.parse(`${validFrom}T00:00:00`) : 0);
    const hi = Math.min(toMs, horizonEnd ? horizonEnd.getTime() : toMs);
    if (hi <= lo) return;
    // Expand the type's ranges to one whole-range window per matching weekday, then chunk each
    // day's range into DURATION-sized blocks spaced by the Fill gap (0 = back-to-back), placing a
    // block only where it doesn't overlap an existing hand-drawn / manual slot on that day. The
    // fixed-grid fillRun wastes colliding positions rather than laying one big range-sized block.
    const gap = Math.max(0, gapMinutes);
    const rangeWins = expandRangesToWindows(type.ranges, new Date(lo), new Date(hi), false, slotMinutes, 500);
    const blocks: BookingWindow[] = [];
    for (const rw of rangeWins) {
      const dayMs = startOfDay(rw.start);
      const dayEndMs = dayMs + 86_400_000;
      // Obstacles fill avoids on this day: existing hand-drawn/manual slots, PLUS the host's busy
      // time (confirmed meetings / bookings / Google events) clamped to this day and converted to
      // minutes-of-day — so ad-hoc fill never lays a slot over a confirmed meeting.
      const obstacles = windows
        .filter((w) => startOfDay(w.start) === dayMs)
        .map((w) => ({ start: minuteOfDay(w.start), end: minuteOfDay(w.end) }));
      for (const b of busyRef.current) {
        const s = Math.max(b.start, dayMs);
        const e = Math.min(b.end, dayEndMs);
        if (e > s) obstacles.push({ start: Math.floor((s - dayMs) / 60000), end: Math.ceil((e - dayMs) / 60000) });
      }
      const { blocks: chunks } = fillRun(minuteOfDay(rw.start), minuteOfDay(rw.end), slotMinutes, gap, obstacles);
      for (const c of chunks) blocks.push(windowFromDayMinutes(dayMs, c.start, c.end));
    }
    // applyWindows drops any block already ended (now-clamping) and keeps back-to-back blocks separate.
    if (blocks.length) applyWindows([...windows, ...blocks]);
  };
  const undoClear = () => {
    if (!lastCleared) return;
    applyWindows([...windows, ...lastCleared]);
    setLastCleared(null);
  };
  // The true bounds of the current view: real month/week/day (not the raw grid span, which for
  // a month includes spillover days); the rolling view falls back to its visible span.
  const currentViewBounds = (): [number, number] | null => {
    if (!calScope) return null;
    const anchor = scopeAnchor();
    if (calScope.view === "dayGridMonth") return monthBounds(anchor);
    if (calScope.view === "timeGridWeek") return weekBounds(anchor);
    if (calScope.view === "timeGridDay") {
      const s = new Date(anchor);
      s.setHours(0, 0, 0, 0);
      return [s.getTime(), s.getTime() + 86_400_000];
    }
    return [Date.parse(calScope.from), Date.parse(calScope.to)]; // rolling
  };
  // Toolbar actions — scoped to whatever the calendar is currently showing.
  const clearCurrentView = () => {
    const bounds = currentViewBounds();
    if (bounds) clearRange(bounds[0], bounds[1], `in this ${viewNoun(calScope?.view)}`);
  };
  const fillCurrentView = (typeId?: string) => {
    const bounds = currentViewBounds();
    if (bounds) fillRange(bounds[0], bounds[1], typeId);
  };
  // The Fill pill shows the chosen type's name (trimmed), else just "Fill".
  const filledType = availabilityPatterns.find((x) => x.id === filledTypeId);
  const fillPillLabel = filledType
    ? filledType.name.length > 12
      ? `${filledType.name.slice(0, 12)}…`
      : filledType.name
    : "Fill";
  // Canvas per-week bin (the row's Monday, ISO).
  const clearWeekFrom = (weekStartIso: string) => {
    const start = Date.parse(weekStartIso);
    clearRange(start, start + 7 * 86_400_000, "in this week");
  };

  const draftWithAi = async () => {
    if (!aiEntitled) {
      navigate("/upgrade");
      return;
    }
    if (title.trim() === "") {
      onError("Give the page a title first. The AI drafts the description from it.");
      return;
    }
    setAiDrafting(true);
    try {
      // Pass the current text so the AI improves + reformats it, not just drafts from the title.
      const draft = await draftBookingDescription({
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setDescription(draft.markdown);
      setLastDraftTokens((draft.input_tokens ?? 0) + (draft.output_tokens ?? 0));
    } catch (e) {
      onError(apiErrorMessage(e, "Couldn't draft a description just now"));
    } finally {
      setAiDrafting(false);
    }
  };

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Live preview: whenever the availability inputs change, ask the server what invitees
  // would see right now (busy already removed). Debounced so typing/dragging doesn't spam.
  React.useEffect(() => {
    if (windows.length === 0) {
      setPreviewSlots([]);
      setBlockedSlots([]);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const handle = window.setTimeout(() => {
      previewBookingSlots({
        title: title.trim() || "preview",
        slot_minutes: slotMinutes,
        timezone,
        valid_from: null, // always live from creation — the host only sets when it stops
        // Derived server-side from the last window and the plan horizon.
        valid_until: null,
        windows,
      })
        .then((slots) => {
          if (cancelled) return;
          // Split the preview: open slots feed the calendar's bookable set; blocked ones (dropped by
          // a meeting clash) render as disabled conflict markers so the host sees WHY they're gone.
          setPreviewSlots(slots.filter((s) => !s.blocked));
          setBlockedSlots(
            slots
              .filter((s) => s.blocked)
              .map((s) => ({ start: s.start, end: s.end, conflictAt: s.conflict_at })),
          );
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewSlots([]);
            setBlockedSlots([]);
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [windows, validFrom, slotMinutes, title, timezone]);

  /**
   * The furthest instant this plan may offer a time at. The canvas stops here, so a host cannot
   * draw availability the server would refuse — it rejects a block past the horizon rather than
   * trimming it, precisely so the builder can never show times no invitee is offered.
   */
  const horizonEnd = React.useMemo(() => {
    if (!limits) return null;
    const d = new Date();
    d.setDate(d.getDate() + limits.max_link_days);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [limits]);



  // The number of bookable slots the invitee would see — the natural ceiling for "max bookings".
  const slotCount = previewSlots.length;
  // The effective ceiling: never more than the slots on offer, and never over the plan's cap.
  const maxCap = Math.min(slotCount, limits?.max_bookings_per_page ?? slotCount);
  // Until the host sets their own cap, max bookings tracks that ceiling; once they've set one,
  // still snap it down if pruning slots (or the plan) drops the ceiling below their number.
  React.useEffect(() => {
    if (slotCount === 0) return;
    if (!maxTouched) {
      setMaxBookings(String(maxCap));
    } else if (maxBookings !== "" && Number(maxBookings) > maxCap) {
      setMaxBookings(String(maxCap));
    }
  }, [slotCount, maxCap, maxTouched, maxBookings]);

  // A booking can't be longer than an availability block it has to fit inside.
  const shortWindows = React.useMemo(
    () => windows.filter((w) => minutesBetween(w.start, w.end) < slotMinutes),
    [windows, slotMinutes],
  );
  const durationValid =
    slotMinutes > 0 &&
    (!limits || (slotMinutes >= limits.min_slot_minutes && slotMinutes <= limits.max_slot_minutes));
  const overRanges = limits != null && windows.length > limits.max_ranges;
  const canSave =
    title.trim().length > 0 &&
    windows.length > 0 &&
    shortWindows.length === 0 &&
    durationValid &&
    !overRanges &&
    !saving;

  // Why the Create button is disabled, spelled out so the host isn't left guessing — title first,
  // since that's the most common blocker and the one they asked to make obvious.
  const createHint = saving
    ? null
    : title.trim().length === 0
      ? "Title is missing"
      : windows.length === 0
        ? "Add at least one time on the calendar for people to book."
        : shortWindows.length > 0
          ? "Some availability blocks are shorter than the booking duration. Fix them above."
          : !durationValid
            ? "Set a valid booking duration."
            : overRanges
              ? "You're over your plan's range limit. Remove a few blocks."
              : null;

  const addTypedBlock = () => {
    setTypedError(null);
    if (!typedDate || !typedDateEnd) {
      setTypedError("Pick a day range.");
      return;
    }
    if (typedDateEnd < typedDate) {
      setTypedError("The last day is before the first.");
      return;
    }
    if (typedFrom >= typedTo) {
      setTypedError("The end time needs to be after the start.");
      return;
    }
    const [fh, fm] = typedFrom.split(":").map(Number);
    const [th, tm] = typedTo.split(":").map(Number);
    const blocks: BookingWindow[] = [];
    const day = new Date(`${typedDate}T00:00:00`);
    const last = new Date(`${typedDateEnd}T00:00:00`);
    while (day <= last) {
      const start = new Date(day);
      start.setHours(fh, fm, 0, 0);
      const end = new Date(day);
      end.setHours(th, tm, 0, 0);
      if (end.getTime() > Date.now()) blocks.push({ start: start.toISOString(), end: end.toISOString() });
      day.setDate(day.getDate() + 1);
    }
    if (blocks.length === 0) {
      setTypedError("Those days have already passed. Pick future dates.");
      return;
    }
    applyWindows([...windows, ...blocks]);
  };

  const save = async () => {
    const req: BookingPageUpsertRequest = {
      title: title.trim(),
      description: description.trim() || null,
      slot_minutes: slotMinutes,
      timezone,
      max_bookings: maxBookings.trim() === "" ? null : Number(maxBookings),
      one_per_invitee: oneEach,
      allow_cancel: allowCancel,
      show_host_avatar: showHostAvatar,
      show_host_banner: showHostBanner,
      valid_from: validFrom ? new Date(`${validFrom}T00:00:00`).toISOString() : null,
      // Derived server-side from the last window and the plan horizon.
      valid_until: null,
      conferencing_provider: (conferencingProvider || null) as ConferenceProvider | null,
      location: physicalOn ? meetingLocation.trim() || null : null,
      windows,
    };
    setSaving(true);
    try {
      const saved = await createBookingPage(req);
      clearDraft(); // the draft became a real page — don't offer to restore it
      onSaved(saved);
    } catch (e) {
      onError(apiErrorMessage(e, "Could not save the booking page"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="comic-shadow relative rounded-xl border border-border bg-card p-5 font-comic-body text-card-foreground">
      {restored && (
        <div
          onTransitionEnd={(e) => {
            // Only the opacity fade, and only once it's fully gone — then remove it.
            if (fading && e.propertyName === "opacity") setRestored(false);
          }}
          className={cn(
            // A floating chip in the card's top-right corner — overlaid (absolute), so its arrival and
            // fade-out never reflow the builder beneath it.
            "absolute right-4 top-4 z-20 flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-brand-blue/30 bg-brand-blue/10 px-3 py-1.5 text-xs shadow-sm backdrop-blur transition-opacity duration-[2500ms] ease-in",
            fading ? "opacity-0" : "opacity-100",
          )}
        >
          <span className="inline-flex items-center gap-1.5 font-medium text-brand-blue">
            <History className="h-3.5 w-3.5 shrink-0" />
            Draft restored. We kept what you were working on.
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={discardDraft}
              className="rounded-full px-2 py-0.5 font-semibold text-brand-blue underline-offset-2 hover:underline"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => setRestored(false)}
              title="Dismiss"
              aria-label="Dismiss"
              className="grid h-6 w-6 place-items-center rounded-full text-brand-blue hover:bg-brand-blue/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}
      <div className="space-y-6">
        {/* Left column (Title + settings) beside the Description. The settings column is narrower
            now that duration + fill-from-type moved to the calendar toolbar, so the Description
            (AI drafting) gets the extra width. md:items-start lines the Description's top up with
            the Title; the left column's height drives the Description height (layout effect) so
            their bottoms line up too. The Title is only as wide as the settings beneath it. */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div ref={railRef} className="space-y-2.5 md:w-2/5 md:shrink-0">
            <div className="space-y-1">
              <Label
                htmlFor="bp-title"
                className="text-xs font-bold uppercase tracking-wide text-foreground"
              >
                Title
              </Label>
              <Input
                id="bp-title"
                value={title}
                maxLength={120}
                placeholder="e.g. 30-min intro call"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="divide-y divide-border rounded-xl bg-muted/40" style={DOTTED_CANVAS}>
            <Panel grouped bodyClassName="space-y-3">
              {/* Helper text folded into each label — a toggle should read at a glance. */}
              <OptionRow
                label="Allow only one booking per person (by email) on this link"
                checked={oneEach}
                onChange={setOneEach}
              />
              <OptionRow
                label="Add a cancel-booking link to the confirmation email"
                checked={allowCancel}
                onChange={setAllowCancel}
              />
            </Panel>

            {/* Meeting location: an optional video provider (a unique link is generated per booking
                at confirmation) and/or a free-text place. The picker sits inline beside the label
                (Panel action) to save a row; the Meet note drops below only when Meet is chosen. */}
            <Panel
              grouped
              id="panel-location"
              title="Meeting location"
              action={
                <div className="min-w-0 max-w-[15rem] flex-1">
                  <MeetingLocationPicker
                    provider={conferencingProvider}
                    onProviderChange={setConferencingProvider}
                    physicalOn={physicalOn}
                    onPhysicalChange={setPhysicalOn}
                    location={meetingLocation}
                    onLocationChange={setMeetingLocation}
                    videoOptions={videoChoices}
                    onConnect={() => {
                      // The draft autosaves itself; this remembers which builder held it.
                      markBuilderReturn("adhoc");
                      navigate("/profile#calendar-integrations");
                    }}
                  />
                </div>
              }
            >
              {conferencingProvider && (
                <p className="text-sm leading-snug text-brand-blue">
                  A unique link is created for each booking and added to the confirmation.
                  Invitees see it once they book.
                </p>
              )}
            </Panel>

            <BuilderSocialNote
              offering="adhoc"
              showAvatar={showHostAvatar}
              showBanner={showHostBanner}
              onShowAvatarChange={setShowHostAvatar}
              onShowBannerChange={setShowHostBanner}
            />

            {/* The "Keep the booking link live until" panel is gone. The host does not choose
                when a link closes: the server derives it from the last time offered and the plan's
                window, because that same date also bounded the free/busy query, so picking a long
                one silently turned the double-booking guard off. Ending a link early is Expire. */}
            {limits && canUpgradeLinkLength(limits.tier) && (
              <Panel grouped id="panel-window">
                <p className="text-sm font-medium text-brand-contrast">
                  Your {titleCase(limits.tier)} plan offers times up to {limits.max_link_days} days
                  ahead. Upgrade to {limits.tier === "FREE" ? "Basic" : "Pro"} to offer them further out.
                </p>
              </Panel>
            )}

            </div>
          </div>

          {/* Description (right, 50%). md:items-start aligns its top with the Title; its textarea
              height is seeded (layout effect) to the left column's height so their bottoms line up
              too, while staying drag-resizable. */}
          <div ref={descColRef} className="flex min-w-0 flex-1 flex-col">
            <Panel
              title="Description (optional)"
              action={
                <div className="flex items-center gap-2">
                  {lastDraftTokens != null && <TokenBadge value={lastDraftTokens} />}
                  <button
                    type="button"
                    onClick={draftWithAi}
                    disabled={aiDrafting}
                    title={
                      aiEntitled
                        ? "Let AI draft from your title, or improve the text you've written"
                        : "Drafting is a Pro feature. See plans"
                    }
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-brand-contrast/30 bg-brand-contrast/10 px-2.5 py-1 text-xs font-semibold text-brand-contrast transition hover:bg-brand-contrast/15 disabled:opacity-60"
                  >
                    <Sparkles className="h-3 w-3 shrink-0" />
                    {aiDrafting
                      ? "Drafting…"
                      : aiEntitled
                        ? "Redraft with AI from title + description"
                        : "Redraft with AI from title + description (Pro)"}
                  </button>
                </div>
              }
            >
              <MarkdownEditor
                id="bp-desc"
                value={description}
                onChange={setDescription}
                maxLength={300}
                placeholder="What's this meeting about? Use the toolbar, or let AI draft a first version."
                fill
                taRef={descTaRef}
              />
            </Panel>
          </div>
        </div>

        {/* Availability — full width */}
        <div className="space-y-2">
          {overRanges && limits && (
            <p className="text-sm text-warning-strong">
              You're over your {titleCase(limits.tier)} plan's {limits.max_ranges} ranges. Clear a
              week or month (below) to get back under.
            </p>
          )}
          {/* relative so the panel can float out into the right gutter on very wide screens */}
          <div className="relative flex flex-col gap-3 md:flex-row">
            {/* The whole builder card is width-capped by its centred column now, so the
                calendar simply fills it — matching the Settings+Description width above and
                staying readably tall instead of flattening across a big-screen viewport. */}
            <div className="min-w-0 flex-1 space-y-3">
            <AvailabilityCalendar
              windows={windows}
              onChange={(next) => applyWindows(next)}
              defaultFrom={typedFrom}
              defaultTo={typedTo}
              showMine={showMine}
              slotMinutes={cursorMinutes}
              onRevealMeetings={() => setShowMine(true)}
              onViewChange={setCalScope}
              onBusyChange={handleBusyChange}
              topNotice={
                lastCleared && lastCleared.length > 0 ? (
                  <div
                    onTransitionEnd={(e) => {
                      if (clearedFading && e.propertyName === "opacity") setLastCleared(null);
                    }}
                    className={cn(
                      "pointer-events-auto flex items-center gap-2 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs shadow-md backdrop-blur transition-opacity duration-[2500ms] ease-in",
                      clearedFading ? "opacity-0" : "opacity-100",
                    )}
                  >
                    <span className="text-muted-foreground">
                      Cleared {lastCleared.length} slot{lastCleared.length === 1 ? "" : "s"}.
                    </span>
                    <button
                      type="button"
                      onClick={undoClear}
                      className="inline-flex items-center gap-1 rounded-full border-2 border-border px-2.5 py-0.5 text-xs font-semibold transition hover:bg-brand-blue/10"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Undo
                    </button>
                    <button
                      type="button"
                      onClick={() => setLastCleared(null)}
                      aria-label="Dismiss"
                      title="Dismiss"
                      className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground hover:bg-muted"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : null
              }
              headerCenter={
                /* Settings summary, centred in the calendar toolbar. "Cursor duration" sizes the block
                   the draw cursor / plain-click places (editable in place); the booking duration lives
                   in the Fill popover now. The location chip jumps to its side panel. */
                <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap text-xs">
                  <ToolbarPopover
                    align="center"
                    active
                    title="Size of the slot the draw cursor places"
                    ariaLabel="Cursor duration"
                    menuClassName="w-72"
                    label={
                      <>
                        <MousePointer2 className="h-3.5 w-3.5 text-brand-blue" /> {durationText(cursorMinutes)}
                      </>
                    }
                  >
                    <div className="space-y-2">
                      <span className="text-xs font-bold uppercase tracking-wide text-foreground">
                        Cursor duration
                      </span>
                      <DurationField minutes={cursorMinutes} onChange={setCursorMinutes} min={5} max={480} label="Slot length" />
                    </div>
                  </ToolbarPopover>
                  <span className="text-muted-foreground/60">|</span>
                  <button
                    type="button"
                    onClick={() => jumpToSetting("panel-location")}
                    title="Change the meeting location"
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 font-medium text-foreground transition hover:border-brand-blue/50 hover:bg-muted"
                  >
                    {conferencingProvider ? (
                      <Link2 className="h-3.5 w-3.5 text-brand-blue" />
                    ) : (
                      <MapPin className="h-3.5 w-3.5 text-warning-strong" />
                    )}
                    {[
                      conferencingProvider ? conferenceProviderLabel(conferencingProvider) : null,
                      physicalOn ? meetingLocation.trim() || "Location" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Add location"}
                  </button>
                </div>
              }
              headerLeft={
                /* Toolbar-left: a Fill pill (pick a type → fills the current view immediately, its
                   name shown on the pill) and, whenever there are slots, an inline Clear pill. The
                   Fill pill only appears when the host has availability patterns to fill from. */
                <div className="flex items-center gap-1.5">
                  {availabilityPatterns.length > 0 && (
                    <ToolbarPopover
                      active={!!filledTypeId}
                      title="Fill the calendar from an availability pattern"
                      ariaLabel="Fill from a pattern"
                      label={
                        <>
                          <Sparkles className="h-3.5 w-3.5 text-brand-blue" /> {fillPillLabel}
                        </>
                      }
                    >
                      {(close) => (
                        <FillFromPatternMenu
                          availabilityPatterns={availabilityPatterns}
                          selectedId={filledTypeId}
                          duration={slotMinutes}
                          onDurationChange={setSlotMinutes}
                          interval={gapMinutes}
                          onIntervalChange={setGapMinutes}
                          onPick={(id) => {
                            selectType(id);
                            fillCurrentView(id);
                          }}
                          close={close}
                        />
                      )}
                    </ToolbarPopover>
                  )}
                  {windows.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        clearCurrentView();
                        setFilledTypeId(""); // reset the Fill pill to an unset state
                      }}
                      title={`Clear this ${viewNoun(calScope?.view)}`}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive-strong"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Clear
                    </button>
                  )}
                </div>
              }
              headerAbove={
                /* Own row above the toolbar: the meetings-overlay toggle on the left, the host's
                   timezone on the right (so the times below are unambiguous). */
                <div className="flex items-center justify-between gap-3 text-xs">
                  <label className="inline-flex items-center gap-1.5 font-medium">
                    <input
                      type="checkbox"
                      checked={showMine}
                      onChange={(e) => setShowMine(e.target.checked)}
                      className="h-4 w-4"
                    />
                    Check to view calendar's confirmed meetings here
                  </label>
                  <span className="whitespace-nowrap text-muted-foreground">
                    All times in <span className="font-medium text-foreground">{timezone}</span>
                  </span>
                </div>
              }
            />

            {/* Short-block warning sits right under the calendar, in context with the block
                it's about, rather than far down near the submit button. */}
            {shortWindows.length > 0 && (
              <div className="rounded-md border-2 border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-strong">
                {shortWindows.length === 1
                  ? "An availability block"
                  : `${shortWindows.length} availability blocks`}{" "}
                ({fmtWindow(shortWindows[0])}
                {shortWindows.length > 1 ? ", …" : ""}) {shortWindows.length === 1 ? "is" : "are"}{" "}
                shorter than the {slotMinutes}-minute booking duration. Lengthen{" "}
                {shortWindows.length === 1 ? "it" : "them"} on the calendar, or shorten the duration.
              </div>
            )}

            {/* Add a block by hand — the secondary path (dragging on the calendar is primary),
                so it's a light, compact card that only takes the width it needs. */}
            <div className="w-fit max-w-full rounded-xl border border-border bg-muted/20 px-3 py-2.5">
              {/* One flat strip: header + fields + button on a single row, note underneath = 2 lines.
                  The per-field labels are dropped — the header and the note below name everything,
                  and each control keeps an ariaLabel for screen readers. */}
              <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-0.5">
                <div
                  className="flex items-center gap-1.5 text-sm font-semibold text-foreground"
                  title="Opens the same hours on every day in the range. This From/To also fills days you select in Month view."
                >
                  <Keyboard className="h-4 w-4" /> Handpick slots here
                </div>
                <div className="w-64">
                  <DateRangeField
                    id="bp-typed-days"
                    value={{ start: typedDate, end: typedDateEnd }}
                    onChange={(r) => {
                      setTypedDate(r.start);
                      setTypedDateEnd(r.end);
                    }}
                    minDate={new Date()}
                    hidePast
                  />
                </div>
                <ClockTimePicker
                  id="bp-typed-from"
                  ariaLabel="From time"
                  value={typedFrom}
                  onChange={setTypedFrom}
                />
                <span className="text-sm text-muted-foreground">–</span>
                <ClockTimePicker
                  id="bp-typed-to"
                  ariaLabel="To time"
                  value={typedTo}
                  onChange={setTypedTo}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addTypedBlock}
                  title="Add this block"
                  aria-label="Add block"
                  className="px-2.5"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {typedError && <p className="mt-1 text-xs text-destructive-strong">{typedError}</p>}
            </div>

            {windows.length === 0 && (
              <p className="text-sm italic text-brand-blue">
                Fill from a pattern above, or drag on the calendar / type a time to add your
                first availability block.
              </p>
            )}

            {/* Preview lives INSIDE the calendar column so it stays calendar-width and flows
                right under the type-a-time card — instead of sitting full-width below the row,
                where a tall availability canvas in the right rail would push it down and leave
                a gap under the type-a-time card. */}
            {windows.length > 0 && (
              <Panel
                title="Preview"
                action={
                  previewLoading ? (
                    <span className="text-xs text-muted-foreground">updating…</span>
                  ) : undefined
                }
              >
                {previewSlots.length === 0 && blockedSlots.length === 0 ? (
                  previewLoading ? (
                    <p className="text-sm italic text-brand-blue">
                      Checking your calendar…
                    </p>
                  ) : null
                ) : (
                  // Read-only (no onPick) — the same calendar the invitee sees, minus booking, plus the
                  // slots dropped by a meeting clash shown as disabled conflict markers.
                  <BookingCalendar
                    slots={previewSlots}
                    blockedSlots={blockedSlots}
                    slotMinutes={slotMinutes}
                    compact
                  />
                )}
                <p className="mt-2 text-sm text-brand-blue">
                  Preview slots are indicative of the pattern and won't be offered on the link where
                  you have a confirmed calendar meeting.
                </p>
              </Panel>
            )}
            </div>
            {/* Availability canvas — only once there are slots. It sits IN FLOW as a right
                rail (md+) or stacks below (mobile), so the calendar shrinks to make room.
                It used to float out past the container at ≥1620px (absolute left-full), which
                only worked while the page was capped and left a gutter to float into. Now the
                page is full-width there is no gutter, so that pushed the panel off-screen and
                produced a horizontal scrollbar. Giving the calendar a little width back is the
                right trade — it's still far wider than the old capped layout. */}
            <div
              className={cn(
                "shrink-0 self-stretch",
                windows.length > 0 ? "w-full md:w-64 xl:w-72" : "hidden",
              )}
              aria-hidden={windows.length === 0}
            >
              {/* Range count sits right above the Availability panel it's counting. */}
              {limits && (
                <div className="mb-1.5 flex justify-end text-xs">
                  <span
                    className={overRanges ? "font-semibold text-warning-strong" : "text-muted-foreground"}
                  >
                    {windows.length} / {limits.max_ranges} ranges
                  </span>
                </div>
              )}
              <AvailabilityCanvas
                windows={windows}
                onRemove={(index) =>
                  setWindows(normalizeWindows(windows.filter((_, i) => i !== index)))
                }
                onClearWeek={clearWeekFrom}
              />
            </div>
          </div>
        </div>

        {/* Max bookings lives here — after the slots exist, since it caps against them. Defaults to
            "all of them" and can only be dialled down. */}
        {slotCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted/20 px-3 py-2">
            <Label htmlFor="bp-max" className="text-sm">
              Max bookings
            </Label>
            <Input
              id="bp-max"
              type="number"
              min={1}
              max={maxCap}
              value={maxBookings}
              onChange={(e) => {
                setMaxTouched(true);
                setMaxBookings(e.target.value);
              }}
              className="h-9 w-20"
            />
            <span className="text-sm text-brand-blue">
              {`of ${slotCount} slot${slotCount === 1 ? "" : "s"} on offer. Invitees can book up to this many.`}
              {limits && maxCap === limits.max_bookings_per_page && (
                <span>{` Plan cap: ${limits.max_bookings_per_page}.`}</span>
              )}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-1">
          <Button onClick={save} disabled={!canSave} title={createHint ?? undefined}>
            {saving ? "Saving…" : existing ? "Save changes" : "Create link"}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {createHint && (
            <span className="text-sm font-medium text-warning-strong">
              {createHint}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
